import React, { useCallback, useEffect, useRef, useState } from "react";
import { X, ChevronLeft, ChevronRight, Sun, ImageOff } from "lucide-react";
import HtmlContent from "./HtmlContent";

// Altezza massima del contenitore descrizione (collassato). Se il contenuto
// sfora, mostriamo il bottone "Leggi di piu".
const COLLAPSED_MAX_PX = 320;

/**
 * Modal di dettaglio condiviso tra blog post e progetti.
 * Layout: immagine + carousel a sinistra, testo/meta a destra.
 * Mobile: stack verticale, immagine sopra.
 *
 * Props:
 *  - open: boolean
 *  - onClose: fn
 *  - title: string
 *  - description: string (whitespace-pre-line, con Leggi di piu se >380 char)
 *  - images: string[]
 *  - badge: string opzionale (es. "Residenziale", chip in alto a sinistra)
 *  - meta: array di { icon: ReactNode, label: string } — pill row sotto il titolo
 */
const DetailModal = ({
  open,
  onClose,
  title = "",
  description = "",
  images = [],
  badge = "",
  meta = [],
}) => {
  const [index, setIndex] = useState(0);
  const [expanded, setExpanded] = useState(false);
  const [overflows, setOverflows] = useState(false);
  const descRef = useRef(null);

  const total = images.length;
  const showNav = total > 1;

  const prev = useCallback(
    () => setIndex((i) => (i - 1 + total) % Math.max(total, 1)),
    [total]
  );
  const next = useCallback(
    () => setIndex((i) => (i + 1) % Math.max(total, 1)),
    [total]
  );

  const handleKey = useCallback(
    (e) => {
      if (e.key === "Escape") onClose?.();
      else if (e.key === "ArrowLeft") prev();
      else if (e.key === "ArrowRight") next();
    },
    [onClose, prev, next]
  );

  useEffect(() => {
    if (!open) return;
    setIndex(0);
    setExpanded(false);
    document.addEventListener("keydown", handleKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", handleKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, handleKey]);

  // Misura reale del DOM: dopo il render, se lo scrollHeight supera
  // COLLAPSED_MAX_PX, sappiamo che c'e' del contenuto da rivelare.
  useEffect(() => {
    if (!open || !descRef.current) {
      setOverflows(false);
      return;
    }
    const el = descRef.current;
    const check = () => {
      setOverflows(el.scrollHeight > COLLAPSED_MAX_PX + 4);
    };
    check();
    // Ri-misura dopo un frame per catturare eventuali reflow
    const t = setTimeout(check, 120);
    return () => clearTimeout(t);
  }, [open, description]);

  if (!open) return null;

  const cover = images[index];

  return (
    <div
      className="fixed inset-0 z-[9999] bg-black/80 backdrop-blur-sm flex items-center justify-center p-2 sm:p-6"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label={title}
    >
      {/* Close in alto a destra fuori dal contenuto */}
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onClose?.();
        }}
        className="absolute top-3 right-3 sm:top-5 sm:right-5 w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-colors backdrop-blur-sm z-10"
        aria-label="Chiudi"
      >
        <X className="w-5 h-5" />
      </button>

      <div
        className="relative w-full max-w-6xl max-h-[95vh] md:max-h-[92vh] bg-white rounded-2xl shadow-2xl flex flex-col md:grid md:grid-cols-[minmax(0,3fr)_minmax(0,2fr)] overflow-y-auto md:overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* ---------- Colonna sinistra: immagine + thumbs ---------- */}
        <div className="relative bg-gray-950 flex flex-col min-w-0">
          <div className="relative flex-none md:flex-1 aspect-[4/3] sm:aspect-video md:aspect-auto md:min-h-[420px] flex items-center justify-center overflow-hidden">
            {cover ? (
              <img
                src={cover}
                alt={title}
                className="w-full h-full object-contain select-none"
                referrerPolicy="no-referrer"
                draggable="false"
              />
            ) : (
              <div className="flex flex-col items-center gap-2 text-gray-500">
                <ImageOff className="w-12 h-12" />
                <p className="text-sm">Nessuna immagine</p>
              </div>
            )}

            {showNav && (
              <>
                <button
                  type="button"
                  onClick={prev}
                  aria-label="Precedente"
                  className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/10 hover:bg-white/25 text-white flex items-center justify-center backdrop-blur-sm transition-colors"
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>
                <button
                  type="button"
                  onClick={next}
                  aria-label="Successiva"
                  className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/10 hover:bg-white/25 text-white flex items-center justify-center backdrop-blur-sm transition-colors"
                >
                  <ChevronRight className="w-5 h-5" />
                </button>
                <div className="absolute bottom-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-black/50 backdrop-blur-sm text-white text-xs font-montserrat font-semibold">
                  {index + 1} / {total}
                </div>
              </>
            )}
          </div>

          {/* Thumbnail strip */}
          {showNav && (
            <div className="bg-black/60 p-2 flex gap-2 overflow-x-auto">
              {images.map((src, i) => (
                <button
                  key={src + i}
                  type="button"
                  onClick={() => setIndex(i)}
                  aria-label={`Vai a immagine ${i + 1}`}
                  className={`relative flex-none w-14 h-14 rounded-md overflow-hidden border-2 transition-colors ${
                    i === index
                      ? "border-[#F4C542]"
                      : "border-transparent hover:border-white/40"
                  }`}
                >
                  <img
                    src={src}
                    alt=""
                    referrerPolicy="no-referrer"
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* ---------- Colonna destra: contenuto ----------
            La grid track e' minmax(0, 2fr): permette al contenuto di
            wrapparsi correttamente invece che spingere fuori dal modale.
            Su mobile scrolla il modal, su desktop scrolla la colonna. */}
        <div className="min-w-0 flex flex-col md:overflow-y-auto md:max-h-[92vh]">
          <div className="p-6 sm:p-8 flex-1">
            {badge && (
              <div className="inline-flex items-center gap-1.5 bg-[#F4C542]/20 text-[#0A1F44] text-xs font-montserrat font-bold px-3 py-1 rounded-full mb-4">
                <Sun className="w-3.5 h-3.5 text-[#F4C542]" />
                {badge}
              </div>
            )}

            <h2 className="text-2xl sm:text-3xl font-montserrat font-bold text-[#0A1F44] leading-tight mb-4">
              {title}
            </h2>

            {meta.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-6">
                {meta.map((m, i) => (
                  <span
                    key={i}
                    className="inline-flex items-center gap-1.5 bg-gray-100 text-[#0A1F44] text-xs font-montserrat font-semibold px-3 py-1.5 rounded-full"
                  >
                    {m.icon}
                    {m.label}
                  </span>
                ))}
              </div>
            )}

            {description && (
              <>
                <p className="text-[11px] font-montserrat font-bold uppercase tracking-widest text-gray-400 mb-2">
                  Descrizione
                </p>
                <div
                  ref={descRef}
                  className="relative overflow-hidden transition-[max-height] duration-200"
                  style={{
                    maxHeight: expanded ? "none" : `${COLLAPSED_MAX_PX}px`,
                  }}
                >
                  <HtmlContent
                    html={description}
                    className="text-sm sm:text-base"
                  />
                  {overflows && !expanded && (
                    <div className="pointer-events-none absolute inset-x-0 bottom-0 h-12 bg-gradient-to-t from-white via-white/80 to-transparent" />
                  )}
                </div>
                {overflows && (
                  <button
                    type="button"
                    onClick={() => setExpanded((v) => !v)}
                    className="mt-3 inline-flex items-center gap-1 text-sm font-montserrat font-bold text-[#0A1F44] hover:text-[#F4C542] transition-colors"
                  >
                    {expanded ? "Riduci" : "Leggi di più"}
                    <ChevronRight
                      className={`w-4 h-4 transition-transform ${
                        expanded ? "-rotate-90" : "rotate-90"
                      }`}
                    />
                  </button>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DetailModal;
